import apiService from './api.service';

export enum RiskLevel {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
}

export enum DischargeStatus {
  ACTIVE = 'Active',
  COMPLETED = 'Completed',
}

export interface HomeCareInstruction {
  title: string;
  description: string;
}

export interface LabDiagnostic {
  test_name: string;
  frequency: string;
  instructions: string;
  priority: string;
  reason?: string;
}

export interface RegularFollowup {
  followup_date: string;
  followup_time: string;
  observations: string[];
  medications: string[];
  lifestyle_recommendations: string[];
}

export interface Referral {
  referral_type: string;
  specialty: string;
  location: string;
  provider_name: string;
  priority: string;
}

export interface Discharge {
  id: string;
  patient_id: string;
  discharge_date: string;
  risk_level: RiskLevel;
  next_followup_due?: string;
  specialty?: string;
  care_manager?: string;
  status: DischargeStatus;
  primary_diagnosis?: string;
  discharge_summary?: string;
  allergies?: string[];
  home_care_instructions?: HomeCareInstruction[];
  lab_diagnostics?: LabDiagnostic[];
  regular_followup?: RegularFollowup;
  referral?: Referral;
  created_at: string;
  updated_at: string;
  patient?: {
    id: string;
    patient_id: string;
    first_name: string;
    last_name: string;
    email: string;
    contact_number: string;
  };
}

export interface CreateDischargeDto {
  patient_id: string;
  discharge_date: string;
  risk_level: RiskLevel;
  next_followup_due?: string;
  specialty?: string;
  care_manager?: string;
  status?: DischargeStatus;
  primary_diagnosis?: string;
  discharge_summary?: string;
  allergies?: string[];
  home_care_instructions?: HomeCareInstruction[];
  lab_diagnostics?: LabDiagnostic[];
  regular_followup?: RegularFollowup;
  referral?: Referral;
}

export interface DischargeFilters {
  patient_id?: string;
  risk_level?: RiskLevel;
  status?: DischargeStatus;
  specialty?: string;
}

export interface DischargeStats {
  total: number;
  byRiskLevel: Record<string, number>;
  byStatus: Record<string, number>;
}

export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

class DischargeService {
  private readonly baseUrl = '/discharge';
  async getAllDischarges(filters?: DischargeFilters): Promise<Discharge[]> {
    const params = new URLSearchParams();
    if (filters?.patient_id) params.append('patient_id', filters.patient_id);
    if (filters?.risk_level) params.append('risk_level', filters.risk_level);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.specialty) params.append('specialty', filters.specialty);
    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    const response = await apiService.get<ApiResponse<Discharge[]>>(url);
    return response.data;
  }

  async getDischargeById(id: string): Promise<Discharge> {
    const response = await apiService.get<ApiResponse<Discharge>>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async getDischargesByPatientId(patientId: string): Promise<Discharge[]> {
    const response = await apiService.get<ApiResponse<Discharge[]>>(`${this.baseUrl}/patient/${patientId}`);
    return response.data;
  }

  async createDischarge(data: CreateDischargeDto): Promise<Discharge> {
    const response = await apiService.post<ApiResponse<Discharge>>(this.baseUrl, data);
    return response.data;
  }

  async updateDischarge(id: string, data: Partial<CreateDischargeDto>): Promise<Discharge> {
    const response = await apiService.put<ApiResponse<Discharge>>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async deleteDischarge(id: string): Promise<void> {
    await apiService.delete<ApiResponse<null>>(`${this.baseUrl}/${id}`);
  }

  async getDischargeStats(): Promise<DischargeStats> {
    const response = await apiService.get<ApiResponse<DischargeStats>>(`${this.baseUrl}/stats`);
    return response.data;
  }

  async createFollowUp(dischargeId: string, payload: any): Promise<any> {
    const response = await apiService.post<ApiResponse<any>>(`${this.baseUrl}/${dischargeId}/followups`, payload);
    return response.data;
  }

  async listFollowUps(dischargeId: string): Promise<any[]> {
    const response = await apiService.get<ApiResponse<any[]>>(`${this.baseUrl}/${dischargeId}/followups`);
    return response.data;
  }
}

const dischargeService = new DischargeService();
export default dischargeService;
